import { useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Store, MapPin, ArrowUpRight } from "lucide-react";

/** Petit encart inséré ENTRE deux sections de la revue de presse :
 *  une offre RÉELLE + un partenaire RÉEL, en format compact. Les données
 *  (offer / partner) sont fournies par le parent pour éviter un appel API
 *  par section. Le placement est compté (channel "press"). */
export default function PressInterstitial({ offer, partner, testId = "press-interstitial" }) {
  useEffect(() => {
    const ids = [];
    if (partner?.id) ids.push(partner.id);
    if (offer?.business_id) ids.push(offer.business_id);
    if (ids.length) {
      api.post("/track/editorial-placement", { business_ids: [...new Set(ids)], channel: "press" }).catch(() => {});
    }
  }, [offer, partner]);

  if (!offer && !partner) return null;
  const img = offer ? (offer.image || (offer.images && offer.images[0]) || null) : null;
  const siteUrl = partner?.website
    ? (partner.website.startsWith("http") ? partner.website : `https://${partner.website}`)
    : null;

  return (
    <div className="my-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-3" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Près de chez vous · Partenaires</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {offer && (
          <Link to={`/offre/${offer.id}`} data-testid={`${testId}-offer`}
                className="group flex items-center gap-3 rounded-lg bg-white border border-slate-200 p-2 hover:border-[#E05638] transition-colors">
            <div className="w-14 h-14 rounded-md overflow-hidden bg-slate-100 shrink-0 grid place-items-center">
              {img && <img src={img} alt={offer.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />}
            </div>
            <div className="min-w-0">
              {offer.discount && <span className="inline-block text-[10px] font-extrabold text-white bg-rose-500 rounded-full px-1.5 py-0.5">{offer.discount}</span>}
              <div className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2">{offer.title}</div>
              {offer.city && <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5"><MapPin size={10} /> {offer.city}</div>}
            </div>
          </Link>
        )}
        {partner && (
          <div data-testid={`${testId}-partner`} className="flex items-center gap-3 rounded-lg bg-white border border-slate-200 p-2">
            <Link to={`/commerce/${partner.id}`} className="shrink-0" aria-label={partner.name}>
              <div className="w-14 h-14 rounded-md overflow-hidden bg-slate-100 border border-slate-200 grid place-items-center">
                {partner.logo || partner.cover_image
                  ? <img src={partner.logo || partner.cover_image} alt={partner.name} loading="lazy" className="w-full h-full object-cover" />
                  : <Store size={18} className="text-slate-300" />}
              </div>
            </Link>
            <div className="min-w-0 flex-1">
              <Link to={`/commerce/${partner.id}`} className="font-semibold text-slate-900 text-sm line-clamp-1 hover:text-[#E05638] transition-colors">{partner.name}</Link>
              {partner.city && <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5"><MapPin size={10} /> {partner.city}</div>}
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <Link to={`/commerce/${partner.id}`} className="inline-flex items-center gap-1 rounded-full bg-orange-50 text-[#E05638] border border-orange-200 px-2 py-0.5 text-[11px] font-semibold hover:bg-orange-100 transition-colors"><Store size={11} /> Mini-site</Link>
                {siteUrl && <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-slate-50 text-slate-700 border border-slate-200 px-2 py-0.5 text-[11px] font-semibold hover:bg-slate-100 transition-colors">Site <ArrowUpRight size={10} className="opacity-60" /></a>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
